import React, { useRef, useState } from "react";
import { Plus, Upload, ChevronLeft, Check, X } from "lucide-react";
import { IMPORT_META } from "../../constants";
import {
  importExt, previewFor, processImportFile, type ImportTargetType,
} from "../../importPipeline";
import { attachFilesToEmails, isAttachmentFileName, type PoolFile } from "../../corpusImport";
import { ImportItem, type ImportItemRecord } from "./ImportItem";
import type { CorpusSet } from "../../types";

const EMAIL_SOURCE_EXTS = ["mbox", "eml", "zip", "json", "csv"];
const isEmailSource = (name: string): boolean => EMAIL_SOURCE_EXTS.includes(importExt(name));

function readDataUrl(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result || ""));
    r.onerror = () => rej(r.error);
    r.readAsDataURL(file);
  });
}

const poolName = (it: ImportItemRecord): string => it.fileName || it.file.name;

/* Re-derive every corpus item's payload from its as-parsed base, applying the
   current pool of attachment files, then annotate each pool file with how
   many email attachments it landed on. Pure — safe to run on every change. */
function associate(list: ImportItemRecord[]): ImportItemRecord[] {
  const pool: PoolFile[] = list
    .filter((it) => it.kind === "attachmentFile" && it.include && it.dataUrl)
    .map((it) => ({ name: poolName(it), dataUrl: it.dataUrl as string }));
  const next = list.map((it) => {
    if (it.kind === "attachmentFile" || it.asType !== "corpus" || !it.payload) return it;
    if (it.status === "processing" || it.status === "error") return it;
    const base = it.basePayload || it.payload;
    const emails = (base.emails || []).map((e: any) => ({
      ...e,
      attachments: (e.attachments || []).map((a: any) => ({ ...a })),
    }));
    attachFilesToEmails(emails, pool);
    const payload = { ...base, emails };
    return { ...it, basePayload: base, payload, preview: previewFor("corpus", payload) };
  });
  return next.map((it) => {
    if (it.kind !== "attachmentFile") return it;
    const key = poolName(it).toLowerCase();
    const matches = next.reduce((n, ci) => {
      if (ci.kind === "attachmentFile" || ci.asType !== "corpus" || !ci.payload) return n;
      return (
        n +
        (ci.payload.emails || []).reduce(
          (m: number, e: any) =>
            m +
            (e.attachments || []).filter(
              (a: any) => String(a.filename || "").toLowerCase() === key,
            ).length,
          0,
        )
      );
    }, 0);
    return { ...it, matches };
  });
}

export interface NewEntityModalProps {
  type: ImportTargetType;
  corpuses: CorpusSet[];
  onClose: () => void;
  onBlank: () => void;
  onImport: (items: ImportItemRecord[]) => void;
}

/* Modal opened from the Library to create a new corpus / eval set / workstream
   bundle. Two paths: start blank, or drop files (parsed → mapped → extracted). */
export function NewEntityModal({
  type, corpuses, onClose, onBlank, onImport,
}: NewEntityModalProps): JSX.Element {
  const meta = IMPORT_META[type];
  const Icon = meta.icon;
  const [mode, setMode] = useState<string>("choose");
  const [items, setItems] = useState<ImportItemRecord[]>([]);
  const [over, setOver] = useState<boolean>(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const busy = items.some((it) => it.status === "processing");
  const ready = items.filter(
    (it) =>
      it.kind !== "attachmentFile" &&
      it.include && it.status && it.status !== "processing" && it.status !== "error",
  );

  const addFiles = async (fileList: FileList | File[]): Promise<void> => {
    const files = Array.from(fileList);
    if (!files.length) return;
    setMode("import");
    /* For corpus imports, document files (pdf/docx/xlsx…) dropped alongside an
       email source become attachment files matched by filename, instead of
       standalone Tier-3 extractions. */
    const corpusContext =
      type === "corpus" &&
      (files.some((f) => isEmailSource(f.name)) ||
        items.some((it) => it.kind !== "attachmentFile" && isEmailSource(poolName(it))));
    const isPoolFile = (f: File): boolean =>
      corpusContext && !isEmailSource(f.name) && isAttachmentFileName(f.name);
    const base = Date.now() + "_" + Math.random().toString(36).slice(2, 5);
    const seeds: ImportItemRecord[] = files.map((f, i) => ({
      id: base + "_" + i,
      file: f,
      fileName: f.name,
      status: "processing",
      include: true,
      name: f.name.replace(/\.[^.]+$/, ""),
      ...(isPoolFile(f) ? { kind: "attachmentFile" as const } : {}),
    }));
    setItems((prev) => [...prev, ...seeds]);
    for (const seed of seeds) {
      try {
        if (seed.kind === "attachmentFile") {
          const dataUrl = await readDataUrl(seed.file);
          setItems((prev) =>
            associate(
              prev.map((it) => (it.id === seed.id ? { ...it, dataUrl, status: "file" } : it)),
            ),
          );
          continue;
        }
        const res = await processImportFile(seed.file, type, corpuses, false);
        setItems((prev) =>
          associate(prev.map((it) => (it.id === seed.id ? { ...it, ...res } : it))),
        );
      } catch (err: any) {
        setItems((prev) =>
          prev.map((it) =>
            it.id === seed.id
              ? { ...it, status: "error", error: String((err && err.message) || err) }
              : it,
          ),
        );
      }
    }
  };
  const switchType = async (item: ImportItemRecord): Promise<void> => {
    setItems((prev) =>
      prev.map((it) => (it.id === item.id ? { ...it, status: "processing" } : it)),
    );
    try {
      const res = await processImportFile(item.file, type, corpuses, true);
      setItems((prev) =>
        associate(
          prev.map((it) =>
            it.id === item.id ? { ...it, ...res, basePayload: undefined } : it,
          ),
        ),
      );
    } catch (err: any) {
      setItems((prev) =>
        prev.map((it) =>
          it.id === item.id
            ? { ...it, status: "error", error: String((err && err.message) || err) }
            : it,
        ),
      );
    }
  };
  const setName = (id: string, name: string): void =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, name } : it)));
  const toggle = (id: string): void =>
    setItems((prev) =>
      associate(prev.map((it) => (it.id === id ? { ...it, include: !it.include } : it))),
    );
  const remove = (id: string): void =>
    setItems((prev) => associate(prev.filter((it) => it.id !== id)));

  return (
    <div className="modal" onClick={onClose}>
      <div className="mbox" onClick={(e) => e.stopPropagation()}>
        <div className="mbhd">
          <div className="logo" style={{ background: "var(--subtle)", color: "var(--fg)" }}>
            <Icon size={15} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="mt">New {meta.label}</div>
            <div className="ms">
              {mode === "choose"
                ? "Start blank, or import from files of any format"
                : "Drop files — each is parsed, mapped, or extracted automatically"}
            </div>
          </div>
          <button className="icon-btn" onClick={onClose}>
            <X size={17} />
          </button>
        </div>

        <div className="scroll" style={{ padding: 0, paddingTop: mode === "import" ? 14 : 0 }}>
          {mode === "choose" && (
            <div className="impchoice">
              <button className="impopt" onClick={onBlank}>
                <div className="it">
                  <Plus size={15} /> Start blank
                </div>
                <div className="id">{meta.blankDesc}</div>
              </button>
              <button className="impopt" onClick={() => setMode("import")}>
                <div className="it">
                  <Upload size={15} /> Import from files
                </div>
                <div className="id">
                  Drop files and the system parses, maps, or extracts the content into the right
                  shape.
                </div>
              </button>
            </div>
          )}

          {mode === "import" && (
            <>
              <div
                className={"impdrop" + (over ? " over" : "")}
                onClick={() => fileRef.current && fileRef.current.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setOver(true);
                }}
                onDragLeave={() => setOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setOver(false);
                  if (e.dataTransfer.files && e.dataTransfer.files.length)
                    addFiles(e.dataTransfer.files);
                }}
              >
                <Upload size={22} />
                <div className="dmt">Drop files here, or click to browse</div>
                <div className="dms">Any format · multiple files · {meta.accept}</div>
                {type === "corpus" && (
                  <div className="dms">
                    Drop real attachment files (PDF · DOCX · XLSX …) together with your corpus —
                    they attach to emails by filename. A .zip of corpus + documents works too.
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files;
                    e.target.value = "";
                    if (f && f.length) addFiles(f);
                  }}
                />
              </div>
              {items.length === 0 && (
                <div className="empty" style={{ padding: "4px 18px 14px" }}>
                  No files yet. {"\u26A1"} Parsed (instant) · {"\uD83D\uDD0D"} Mapped (heuristic) ·{" "}
                  {"\uD83E\uDD16"} Extracted (LLM).
                </div>
              )}
              {items.map((it) => (
                <ImportItem
                  key={it.id}
                  it={it}
                  target={type}
                  onName={setName}
                  onToggle={toggle}
                  onRemove={remove}
                  onSwitch={switchType}
                />
              ))}
            </>
          )}
        </div>

        <div className="mbft">
          {mode === "import" && (
            <button className="btn btn-ghost btn-sm" onClick={() => setMode("choose")}>
              <ChevronLeft size={14} /> Back
            </button>
          )}
          <span className="spacer" />
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            Cancel
          </button>
          {mode === "import" && (
            <button
              className="btn btn-primary btn-sm"
              disabled={busy || ready.length === 0}
              onClick={() => onImport(items)}
            >
              {busy ? (
                <>
                  <span className="spin" /> Processing…
                </>
              ) : (
                <>
                  <Check size={14} /> Import {ready.length} item{ready.length === 1 ? "" : "s"}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
